import { useState, useEffect } from 'react';
import { talentAPI, Talent, TalentKategori } from '@/services/api';
import { SelectedTalent } from '@/components/common/TalentSelector';
import type { Oppmote } from '@/services/api/types';
import { formatTime } from '../utils/timeUtils';

export interface OppmoteFormData {
  id?: number;
  navn: string;
  beskrivelse: string;
  tidspunktMode: 'minutterFør' | 'faktiskTid';
  minutterFør: number;
  faktiskTid: string;
  rekkefølge: number;
  selectedTalents: SelectedTalent[];
}

interface UseOppmoteFormProps {
  oppmote: Oppmote[];
  allTalents: Talent[];
  allKategorier: TalentKategori[];
}

export const useOppmoteForm = ({ oppmote, allTalents, allKategorier }: UseOppmoteFormProps) => {
  const [formData, setFormData] = useState<OppmoteFormData>({
    navn: '',
    beskrivelse: '',
    tidspunktMode: 'minutterFør',
    minutterFør: 30,
    faktiskTid: '10:00',
    rekkefølge: 0,
    selectedTalents: [],
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const openDialog = (item?: Oppmote) => {
    if (item) {
      let selectedTalents: SelectedTalent[] = [];
      if (item.talenter && item.talenter.length > 0) {
        selectedTalents = item.talenter.map(t => {
          const isGroup = t.er_gruppe;

          if (isGroup) {
            const kategori = allKategorier.find(k => 
              k.navn === t.talent_navn || 
              k.path === t.talent_kategori_sti ||
              (t.talent_kategori_sti && t.talent_kategori_sti.includes(k.navn))
            );
            return {
              id: kategori?.id || 0,
              navn: t.talent_navn,
              erKategori: true,
              kategoriSti: t.talent_kategori_sti || undefined,
            };
          } else {
            const talent = allTalents.find(at => at.navn === t.talent_navn);
            return {
              id: talent?.id || 0,
              navn: t.talent_navn,
              erKategori: false,
              kategoriSti: talent?.kategori_navn || undefined,
            };
          }
        });
      }
      
      setFormData({
        id: item.id,
        navn: item.navn,
        beskrivelse: item.beskrivelse || '',
        tidspunktMode: item.er_relativ ? 'minutterFør' : 'faktiskTid',
        minutterFør: item.minutter_for_start,
        faktiskTid: formatTime(item.tidspunkt, 'HH:mm'),
        rekkefølge: item.rekkefølge,
        selectedTalents,
      });
      setEditingId(item.id);
    } else {
      setFormData({
        navn: '',
        beskrivelse: '',
        tidspunktMode: 'minutterFør',
        minutterFør: 30,
        faktiskTid: '10:00',
        rekkefølge: oppmote.length,
        selectedTalents: [],
      });
      setEditingId(null);
    }
  };

  const closeDialog = () => {
    setEditingId(null);
    setFormError(null);
  };

  const resetForm = () => {
    setFormData({
      navn: '',
      beskrivelse: '',
      tidspunktMode: 'minutterFør',
      minutterFør: 30,
      faktiskTid: '10:00',
      rekkefølge: oppmote.length,
      selectedTalents: [],
    });
    setEditingId(null);
    setFormError(null);
  };

  return {
    formData,
    setFormData,
    editingId,
    formError,
    setFormError,
    openDialog,
    closeDialog,
    resetForm,
  };
};

